import { useSelector } from 'react-redux';
import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { keys } from 'fp-ts/Record';
import { keepPreviousData } from '@tanstack/react-query';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DetailedUser, useRepositoryInfiniteQuery } from '@mews-commander/data-access';
import { Loader } from '@mews-commander/ui';
import { Button, StackItem } from '@optimus-web/core';
import { omitFalsyValues } from '@mews/fp';
import { EnterpriseProfilePrivileges } from '@mews-commander/legacy-data-structures';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import {
    isCurrentProfileAdmin,
    getCurrentScopePrivileges,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { createListItems, FormOption, orderByLabel } from '../../utils';
import { FieldNames } from '../FieldNames';

export const RolesField = ({ disabled, detailedUser }: { disabled?: boolean; detailedUser?: DetailedUser }) => {
    const enterpriseId = useCurrentEnterpriseId();
    const { fetchNextPage, hasNextPage, isFetching } = useRolesData({ enterpriseId });
    const rolesOptions = useRolesOptions();

    const handleLoadMoreClick = () => {
        if (isFetching) return;

        void fetchNextPage();
    };

    return (
        <FormSelectField
            name={FieldNames.RoleIds}
            label={<LocalizedText textKey={TextKey.Roles} />}
            size="medium"
            disabled={disabled}
            multiselect
            required
        >
            {createListItems(rolesOptions.concat(disabled ? getLocalRoles(detailedUser) : []))}
            {hasNextPage && (
                // TODO: https://mews.atlassian.net/browse/OPS-18901
                // avoid emits an empty option value to the child `Select`
                <StackItem padding="100" onClick={(event) => event.stopPropagation()}>
                    <Button type="button" variant="ghost" onClick={handleLoadMoreClick} wide>
                        {isFetching ? <Loader size="small" /> : <LocalizedText textKey={TextKey.More} />}
                    </Button>
                </StackItem>
            )}
        </FormSelectField>
    );
};

const useRolesOptions = (): FormOption[] => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data } = useRolesData({ enterpriseId });
    const availableProfileRoles = data?.pages.flatMap((page) => page.data) || [];
    const currentScopePrivileges: Record<string, boolean> = useSelector(getCurrentScopePrivileges);
    const isUserAdmin = useSelector(isCurrentProfileAdmin);
    const canAccessAllEnterprises = useSelector(hasPlatformScopeWithAccessAllEnterprises);

    const enabledPrivileges = pipe(
        currentScopePrivileges,
        record.filter((value) => value === true),
        record.keys
    );

    const enabledRoleIds = pipe(
        availableProfileRoles,
        array.filter((role) =>
            pipe(
                role.privileges as typeof EnterpriseProfilePrivileges,
                omitFalsyValues,
                keys,
                array.every((privilege) => {
                    // There is inconsistency, see https://app.getguru.com/card/igjrMEET/Privileges
                    if (privilege === 'manageProfiles') {
                        return enabledPrivileges.includes('manageEmployees');
                    }
                    return enabledPrivileges.includes(privilege);
                })
            )
        ),
        array.map((role) => role.id)
    );

    return pipe(
        availableProfileRoles,
        array.map((role) => ({
            key: role.id,
            value: role.id,
            label: role.name,
            disabled: !canAccessAllEnterprises && !isUserAdmin && !enabledRoleIds.includes(role.id),
        })),
        array.sortBy([orderByLabel])
    );
};

const getLocalRoles = (detailedUser: DetailedUser | undefined): FormOption[] =>
    pipe(
        detailedUser?.roles || [],
        array.map((role) => ({
            key: role.id || '',
            value: role.id,
            label: role.name || '',
        })),
        array.sortBy([orderByLabel])
    );

const PAGE_SIZE = 25;

const useRolesData = ({ enterpriseId }: { enterpriseId: string }) =>
    useRepositoryInfiniteQuery(
        'roles',
        'getAll',
        ({ pageParam: pageIndex }) => ({
            pagination: {
                pageIndex,
                pageSize: PAGE_SIZE,
            },
            enterpriseId,
        }),
        {
            placeholderData: keepPreviousData,
            staleTime: 0, // legacy endpoint also used in redux with no proper cache invalidation
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                const totalLoaded = PAGE_SIZE * allPages.length;
                return lastPage.totalRowCount > totalLoaded ? allPages.length : undefined;
            },
        }
    );
